import { BaseFeature } from '../../core/base-feature.js';

/**
 * ChessFeature - Frontend feature for Chess game
 *
 * Admin controls: AI engine analysis, unlimited undo, sabotage mechanics.
 * White (frontend) vs Black (desktop) with omniscient advantage.
 */
export class ChessFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        this.gameState = {
            gameState: 'idle',
            board: null,
            turn: null,
            moveHistory: [],
            capturedPieces: { w: [], b: [] },
            isCheck: false,
            status: 'idle',
            winner: null,
            timers: { w: 300000, b: 300000 },
            frozenPieces: [],
            sabotageCount: 0,
            desktopHover: null,
            fen: null
        };

        this.stateUpdateCallbacks = new Set();
        this.gameEndCallbacks = new Set();
        this.updateCallbacks = new Set();
    }

    static getMetadata() {
        return {
            id: 'chess',
            name: 'Chess',
            version: 'v1.0',
            tier: 'external',
            capabilities: [
                'start',
                'move',
                'force_move',
                'set_piece',
                'freeze_piece',
                'pressure_time',
                'set_timer_multipliers',
                'undo_move',
                'sabotage',
                'get_state',
                'get_analysis',
                'end'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Chess] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                return this._handleStateUpdate(payload);
            case 'initial_state':
                return this._handleInitialState(payload);
            default:
                console.warn(`[Chess] Unknown command: ${command}`);
        }
    }

    // ==================== Real-time Event Handlers ====================

    _handleStateUpdate(payload) {
        if (!payload) return;

        switch (payload.type) {
            case 'game_started':
                this._mergeState(payload);
                break;
            case 'move_made':
                this._mergeState(payload);
                break;
            case 'move_undone':
                this._mergeState(payload);
                break;
            case 'sabotage':
            case 'force_move':
            case 'set_piece':
            case 'freeze_piece':
            case 'time_pressure':
            case 'timer_multipliers_set':
            case 'timer_tick':
                this._mergeState(payload);
                break;
            case 'analysis_result':
                this.gameState.lastAnalysis = payload.analysis;
                break;
            case 'desktop_hover':
                this.gameState.desktopHover = payload.hover;
                break;
            case 'game_ended':
                this._mergeState(payload);
                this._notifyGameEnd(payload);
                break;
        }

        this._notifyStateUpdate(payload);
        this._notifyUpdate();
    }

    _handleInitialState(payload) {
        if (payload && payload.state) {
            this._mergeState(payload.state);
        }
        this._notifyUpdate();
    }

    _mergeState(data) {
        if (data.board) this.gameState.board = data.board;
        if (data.turn !== undefined) this.gameState.turn = data.turn;
        if (data.moveHistory) this.gameState.moveHistory = data.moveHistory;
        if (data.capturedPieces) this.gameState.capturedPieces = data.capturedPieces;
        if (data.isCheck !== undefined) this.gameState.isCheck = data.isCheck;
        if (data.status !== undefined) this.gameState.status = data.status;
        if (data.winner !== undefined) this.gameState.winner = data.winner;
        if (data.timers) this.gameState.timers = data.timers;
        if (data.frozenPieces) this.gameState.frozenPieces = data.frozenPieces;
        if (data.sabotageCount !== undefined) this.gameState.sabotageCount = data.sabotageCount;
        if (data.desktopHover !== undefined) this.gameState.desktopHover = data.desktopHover;
        if (data.timerMultiplier) this.gameState.timerMultiplier = data.timerMultiplier;
        if (data.fen) this.gameState.fen = data.fen;
        if (data.gameState) this.gameState.gameState = data.gameState;
        if (data.sessionId) this.gameState.sessionId = data.sessionId;
    }

    // ==================== Command Methods ====================

    async startGame(config = {}) {
        this.sendMessage('start', config);
    }

    async makeMove(from, to, promotion) {
        this.sendMessage('move', { from, to, promotion });
    }

    /**
     * Force a move with no validation. Turn always flips to the opposite of the moved
     * piece's color, so control returns to whichever side wasn't just touched.
     */
    async forceMove(from, to, promotion) {
        this.sendMessage('force_move', { from, to, promotion });
    }

    /** Replace a board square. `piece=null` removes; otherwise letter (UPPER=white, lower=black). */
    async setPiece(row, col, piece) {
        this.sendMessage('set_piece', { row, col, piece });
    }

    async freezePiece(row, col, turns = 3) {
        this.sendMessage('freeze_piece', { row, col, turns });
    }

    async pressureTime(seconds = 30) {
        this.sendMessage('pressure_time', { seconds });
    }

    /**
     * Adjust per-side clock drain multipliers. w<1 slows the frontend's own clock; b>1
     * accelerates the desktop's clock. Defaults: w=1.0, b=2.0 (preserving the existing
     * secret 2× desktop multiplier).
     */
    async setTimerMultipliers(w, b) {
        this.sendMessage('set_timer_multipliers', { w, b });
    }

    async undoMove() {
        this.sendMessage('undo_move', {});
    }

    async sabotage(action, params = {}) {
        this.sendMessage('sabotage', { action, params });
    }

    async getGameState() {
        this.sendMessage('get_state', {});
    }

    async getAnalysis(depth = 5) {
        this.sendMessage('get_analysis', { depth });
    }

    async endGame(reason = 'resigned') {
        this.sendMessage('end', { reason });
    }

    // ==================== Event Subscriptions (for React) ====================

    onStateUpdate(callback) {
        this.stateUpdateCallbacks.add(callback);
        return () => this.stateUpdateCallbacks.delete(callback);
    }

    onGameEnd(callback) {
        this.gameEndCallbacks.add(callback);
        return () => this.gameEndCallbacks.delete(callback);
    }

    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    _notifyStateUpdate(data) {
        for (const cb of this.stateUpdateCallbacks) {
            try { cb(data); } catch (e) { console.error('[Chess] State update callback error:', e); }
        }
    }

    _notifyGameEnd(data) {
        for (const cb of this.gameEndCallbacks) {
            try { cb(data); } catch (e) { console.error('[Chess] Game end callback error:', e); }
        }
    }

    _notifyUpdate() {
        const data = { state: this.gameState };
        for (const cb of this.updateCallbacks) {
            try { cb(data); } catch (e) { console.error('[Chess] Update callback error:', e); }
        }
    }

    // ==================== Getters ====================

    getState() { return this.gameState; }
    isActive() { return this.gameState.gameState === 'active'; }

    async cleanup() {
        console.log('[Chess] Cleaning up...');
        this.gameState = {
            gameState: 'idle', board: null, turn: null, moveHistory: [],
            capturedPieces: { w: [], b: [] }, isCheck: false, status: 'idle',
            winner: null, timers: { w: 300000, b: 300000 }, frozenPieces: [],
            sabotageCount: 0, desktopHover: null, fen: null
        };
        this.stateUpdateCallbacks.clear();
        this.gameEndCallbacks.clear();
        this.updateCallbacks.clear();
    }
}
